import { Component, ViewChild, Input, EventEmitter, Output, OnChanges, OnInit, signal } from "@angular/core";
import { BusinessDevelopmentContactModel, DropdownModel, IBusinessDevelopmentContactModel, IBusinessDevelopmentAccountModel, IDropdownModel, IJobGridModel, IBusinessDevelopmentContactRoleModel, ISurveyModel, IROIExpenseModel, IChecklistGroupModel, IChecklistItemModel } from "@models";
import { SlickToastService, SlickDialogComponent } from "slick-components";
import { BusinessDevelopmentService, ChecklistsService, GlobalsService, LookupService, ROIService, SleepService, SurveysService } from "@services";

import { BusinessDevelopmentNotesComponent } from "../business-development-notes/business-development-notes.component";
import { BusinessDevelopmentAccountEditDialogComponent, BusinessDevelopmentContactEditDialogComponent } from "../business-development-components.module";
import { BusinessDevelopmentAccountTypesDialogComponent } from "./business-development-account-types-dialog/business-development-account-types-dialog.component";
import { EmailerDialogComponent } from "@components";

@Component({
    selector: "business-development-account-view",
    templateUrl: "business-development-account-view.component.html",
    providers: [SlickToastService, SurveysService, ROIService, ChecklistsService],
    standalone: false
})
export class BusinessDevelopmentAccountViewComponent implements OnChanges, OnInit {
	@Input() businessDevelopmentAccount: IBusinessDevelopmentAccountModel;
	@Output() businessDevelopmentAccountChange: EventEmitter<IBusinessDevelopmentAccountModel> = new EventEmitter<IBusinessDevelopmentAccountModel>();

	@ViewChild("businessDevelopmentNotesRef", { static: false }) businessDevelopmentNotesRef: BusinessDevelopmentNotesComponent;
	@ViewChild("businessDevelopmentAccountEditDialog") businessDevelopmentAccountEditDialog: BusinessDevelopmentAccountEditDialogComponent;
	@ViewChild("businessDevelopmentAccountTypesDialog") businessDevelopmentAccountTypesDialog: BusinessDevelopmentAccountTypesDialogComponent;
	@ViewChild("businessDevelopmentContactEditDialog") businessDevelopmentContactEditDialog: BusinessDevelopmentContactEditDialogComponent;
	@ViewChild("emailerDialogRef") emailerDialogRef: EmailerDialogComponent;
	@ViewChild('recordVisitDialog') recordVisitDialog: SlickDialogComponent;
	@ViewChild("contactChecklistDialogRef") contactChecklistDialogRef: SlickDialogComponent;

	jobs: IJobGridModel[];

	marketingEmployees: IDropdownModel[];
	accountTypes: IDropdownModel[];
	carriers: IDropdownModel[];
	surveys: ISurveyModel[];
	roiExpenses: IROIExpenseModel[];
	roiExpensesTotal: number;

	roiCalcTypes: IDropdownModel[] = [
		new DropdownModel('Estimates', 'Estimates'),
		new DropdownModel('Invoices', 'Invoices'),
		new DropdownModel('Payments Received', 'Payments Received')]
	// 
	selectedROICalcTypes: string[] = ['Estimates']; 
	selectedROICalcType: string = 'Estimates'; 
	roiCalcOpenTotal: number;
    roiCalcOpenPercent: number;
	roiCalcClosedTotal: number;
	roiCalcClosedPercent: number;
	roiCalcDescription: string;

	readonly businessDevelopmentAccountChecklistGroups = signal<IChecklistGroupModel[]>(null);
	readonly businessDevelopmentContactsChecklistGroups = signal<IChecklistGroupModel[]>(null);
	readonly contactChecklistGroups = signal<IChecklistGroupModel[]>(null);


	visitDate: Date;
	visitNote: string = '';
	isValidVisitDate: boolean = true; 
	visitSpinnerStatus: string;
	spinnerStatus1: string;

	canSetCommissionEligible: boolean = GlobalsService.checkPermission("BusinessDevelopmentAccounts", "CanSetCommissionEligible")
	canUpdateAccounts: boolean = GlobalsService.checkPermission("BusinessDevelopmentAccounts", "Update");
	canDeleteAccounts: boolean = GlobalsService.checkPermission("BusinessDevelopmentAccounts", "Delete");
	canAddNotes: boolean = GlobalsService.checkPermission("BusinessDevelopmentNotes", "Create");
	canViewNotes: boolean = GlobalsService.checkPermission("BusinessDevelopmentNotes", "View") || GlobalsService.checkPermission("BusinessDevelopmentSystemNotes", "View");
	canViewJobs: boolean = GlobalsService.checkPermission("Jobs", "View");

	constructor(private businessDevelopmentService: BusinessDevelopmentService,
		private surveysService: SurveysService,
		private roiService: ROIService,
		private checklistsService: ChecklistsService,
		private lookupService: LookupService,
		private slickToastService: SlickToastService) {

	}

	async ngOnInit() {
		this.marketingEmployees = await this.lookupService.getMarketingEmployeesForDropdown(this.businessDevelopmentAccount?.marketingEmployeeId);
		this.accountTypes = await this.lookupService.getBusinessDevelopmentAccountTypesForDropdown();
		this.carriers = await this.lookupService.getInsuranceCarriersForDropdown(this.businessDevelopmentAccount?.carrierId);
	}

	async ngOnChanges() {
		this.jobs = null;
		this.surveys = null;
		this.roiExpenses = null;


		if (this.businessDevelopmentAccount) {
			// Reload so that this account's marketing rep stays in the list even if they've been inactivated
			this.marketingEmployees = await this.lookupService.getMarketingEmployeesForDropdown(this.businessDevelopmentAccount.marketingEmployeeId);

			this.businessDevelopmentAccountChecklistGroups.set(null);
			this.businessDevelopmentContactsChecklistGroups.set(null);

			const responses = await Promise.all([
				this.businessDevelopmentService.getJobsForBusinessDevelopmentAccount(this.businessDevelopmentAccount.businessDevelopmentAccountId),
				this.surveysService.getSurveysForBusinessDevelopmentAccount(this.businessDevelopmentAccount.businessDevelopmentAccountId),
				this.roiService.getROIExpensesForBusinessDevelopmentAccount(this.businessDevelopmentAccount.businessDevelopmentAccountId),
				this.checklistsService.getChecklistGroupsForBusinessDevelopmentAccount(this.businessDevelopmentAccount.businessDevelopmentAccountId),
				this.checklistsService.getAllChecklistGroupsForBusinessDevelopmentContacts(this.businessDevelopmentAccount.businessDevelopmentAccountId)
			]);
			this.jobs = responses[0];
			this.surveys = responses[1];
			this.roiExpenses = responses[2];
			this.businessDevelopmentAccountChecklistGroups.set(responses[3]);
			this.businessDevelopmentContactsChecklistGroups.set(responses[4]);

			this.roiExpensesTotal = 0;
			if (this.roiExpenses) {
				this.roiExpensesTotal = this.roiExpenses.reduce((total, expense) => total + expense.expenseAmount, 0);
				this.recalcROI();
			}

		}
	}

	async editAccount() {
		const editedAccount = await this.businessDevelopmentAccountEditDialog.showDialog(this.businessDevelopmentAccount);

		if (editedAccount) {
			this.businessDevelopmentAccount = editedAccount;
			this.businessDevelopmentAccountChange.emit(this.businessDevelopmentAccount);

			this.lookupService.refreshBusinessAccounts();
			this.lookupService.refreshReferrals();
		}
	}

	async checklistItemChanged(checklistItem: IChecklistItemModel) {
		await SleepService.sleep();
		await this.checklistsService.updateChecklistItem(checklistItem);
	}

	async addNewContact() {
		let newAccount = new BusinessDevelopmentContactModel(this.businessDevelopmentAccount.businessDevelopmentAccountId);

		newAccount = await this.businessDevelopmentContactEditDialog.showDialog(newAccount);

		if (newAccount) {
			if (!this.businessDevelopmentAccount.businessDevelopmentContacts)
				this.businessDevelopmentAccount.businessDevelopmentContacts = [];
			this.businessDevelopmentAccount.businessDevelopmentContacts.unshift(newAccount);

			const allContactChecklists = await this.checklistsService.getAllChecklistGroupsForBusinessDevelopmentContacts(this.businessDevelopmentAccount.businessDevelopmentAccountId)
			this.businessDevelopmentContactsChecklistGroups.set(allContactChecklists);

			await this.lookupService.refreshReferrals();
		}
	}

	async onAccountTypeChanged(accountType: IDropdownModel) {
		if (!accountType) {
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeId = null;
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeDescription = null;
		}
		else {
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeId = accountType.id;
			this.businessDevelopmentAccount.businessDevelopmentAccountTypeDescription = accountType.text;
		}

		this.businessDevelopmentAccount = await this.businessDevelopmentService.updateBusinessDevelopmentAccount(this.businessDevelopmentAccount);
		this.businessDevelopmentAccountChange.emit(this.businessDevelopmentAccount);
		this.slickToastService.showSuccess("Account Type Updated");
	}

	async onCarrierChanged(carrier: IDropdownModel) {
		if (!carrier) {
			this.businessDevelopmentAccount.carrierId = null;
			this.businessDevelopmentAccount.carrierDisplayName = null;
		}
		else {
			this.businessDevelopmentAccount.carrierId = carrier.id;
			this.businessDevelopmentAccount.carrierDisplayName = carrier.text;
		}

		this.businessDevelopmentAccount = await this.businessDevelopmentService.updateBusinessDevelopmentAccount(this.businessDevelopmentAccount);
		this.businessDevelopmentAccountChange.emit(this.businessDevelopmentAccount);
		this.slickToastService.showSuccess("Carrier Updated");
	}

	async editAccountTypes() {
		await this.businessDevelopmentAccountTypesDialog.showDialog();

		this.accountTypes = await this.lookupService.getBusinessDevelopmentAccountTypesForDropdown();

    }

	async editContact(accountContact: IBusinessDevelopmentContactModel) {
		const editedAccountContact = await this.businessDevelopmentContactEditDialog.showDialog(accountContact);

		if (editedAccountContact) {
			const idx = this.businessDevelopmentAccount.businessDevelopmentContacts.findIndex(x => x.uuid === editedAccountContact.uuid);
			if (idx >= 0)
				this.businessDevelopmentAccount.businessDevelopmentContacts[idx] = editedAccountContact;

			this.lookupService.refreshReferrals();
		}
	}

	async deleteContact(accountContact: IBusinessDevelopmentContactModel) {
		const result = confirm("Are you sure you want to delete this contact?");

		if (result === true) {
			await this.businessDevelopmentService.deleteBusinessDevelopmentContact(accountContact.businessDevelopmentContactId);
			this.businessDevelopmentAccount.businessDevelopmentContacts = this.businessDevelopmentAccount.businessDevelopmentContacts.filter(x => x.uuid !== accountContact.uuid);

			this.lookupService.refreshReferrals();
		}
	}
	async onMarketingEmployeeChanged(marketingEmployee: IDropdownModel) {
		if (!marketingEmployee) {
			this.businessDevelopmentAccount.marketingEmployeeId = null;
			this.businessDevelopmentAccount.marketingEmployeeFullName = null;
		}
		else {
			this.businessDevelopmentAccount.marketingEmployeeId = marketingEmployee.id;
			this.businessDevelopmentAccount.marketingEmployeeFullName = marketingEmployee.text;
		}

		this.businessDevelopmentAccount = await this.businessDevelopmentService.updateBusinessDevelopmentAccount(this.businessDevelopmentAccount);
		this.businessDevelopmentAccountChange.emit(this.businessDevelopmentAccount);
		this.slickToastService.showSuccess("Account Updated");
	}

	async onIsReferralChanged() {
		this.businessDevelopmentAccount.isReferral = !this.businessDevelopmentAccount.isReferral;
		this.businessDevelopmentAccount = await this.businessDevelopmentService.updateBusinessDevelopmentAccount(this.businessDevelopmentAccount);
		this.businessDevelopmentAccountChange.emit(this.businessDevelopmentAccount);
		this.slickToastService.showSuccess("Account Updated");

		this.lookupService.refreshReferrals();
	}

	async onIsCarrierChanged() {
		this.businessDevelopmentAccount.isCarrier = !this.businessDevelopmentAccount.isCarrier;
		await this.updateAccount();

		this.lookupService.refreshBusinessAccounts();
	}

	async onIsAdjusterChanged() {
		this.businessDevelopmentAccount.isAdjuster = !this.businessDevelopmentAccount.isAdjuster;
		await this.updateAccount();

		this.lookupService.refreshBusinessAccounts();
	}

	async onIsAgentChanged() {
		this.businessDevelopmentAccount.isAgent = !this.businessDevelopmentAccount.isAgent;
		await this.updateAccount();

		this.lookupService.refreshBusinessAccounts();
	}

	async onIsCommissionEligibleChanged() {
		this.businessDevelopmentAccount.isCommissionEligible = !this.businessDevelopmentAccount.isCommissionEligible;
		await this.updateAccount();

		this.lookupService.refreshBusinessAccounts();
	}

	async updateAccount() {
		await SleepService.sleep();

		this.businessDevelopmentAccount = await this.businessDevelopmentService.updateBusinessDevelopmentAccount(this.businessDevelopmentAccount);
		this.businessDevelopmentAccountChange.emit(this.businessDevelopmentAccount);
		this.slickToastService.showSuccess("Account Updated");
	}

	async emailSurvey(surveyId: number) {
		const emailerModel = await this.surveysService.generateSurveyEmail(surveyId);

		const suggestedRecipients = this.businessDevelopmentService.getBusinessDevelopmentRecipientSuggestions(this.businessDevelopmentAccount);
		this.emailerDialogRef.showDialog(emailerModel, null, suggestedRecipients);
	}

	onNoteAdded() {
		this.businessDevelopmentAccountChange.emit(this.businessDevelopmentAccount);
	}

	onRecordVisitClick() {
		this.visitSpinnerStatus = "reset";

		const now = new Date();
		// Auto-populate with current date & time
		this.visitDate = now;
		this.visitNote = '';

		this.recordVisitDialog.showDialog();
	}

	async onSaveVisit() {
		await SleepService.sleep();

		this.visitSpinnerStatus = "reset";

		// For safety, re-validate time
		if (!this.isValidVisitDate) {
			this.visitSpinnerStatus = "error";
			return;
		}

		this.visitSpinnerStatus = "spin";

		await this.businessDevelopmentService.recordVisit(
			this.businessDevelopmentAccount.businessDevelopmentAccountId,
			this.visitDate,
			this.visitNote
		)

		// Reload the account
		this.businessDevelopmentAccount = await this.businessDevelopmentService.getBusinessDevelopmentAccount(this.businessDevelopmentAccount.businessDevelopmentAccountId);
		this.businessDevelopmentNotesRef.refreshUserNotes();

		this.visitSpinnerStatus = "ok";
		await SleepService.sleep(500);

		// Hide the dialog after saving
		this.recordVisitDialog.hideDialog();
	}

	onCancelVisit() {
		this.visitSpinnerStatus = "reset";

		this.recordVisitDialog.hideDialog();
	}

	async onROICalcTypeChanged(roiCalcType: any) {
		this.selectedROICalcTypes = [roiCalcType.id];
		this.selectedROICalcType = roiCalcType.id;

		this.recalcROI();
	}

	recalcROI() {
        const acceptedjobs = this.jobs.filter(x => (x.jobStatusDescription !== 'Lead' && x.jobStatusDescription !== 'Not Accepted'));
		const openJobs = acceptedjobs.filter(x => x.isOpen === true);
		const closedJobs = acceptedjobs.filter(x => x.isClosed === true);

		switch (this.selectedROICalcType) {
			case 'Estimates':
				this.roiCalcDescription = "Revenue";
				this.roiCalcOpenTotal = openJobs.reduce((total, expense) => total + expense.estimateTotal, 0);
				this.roiCalcOpenPercent = (this.roiExpensesTotal > 0) ? (this.roiCalcOpenTotal - this.roiExpensesTotal) / this.roiExpensesTotal : 0;
				this.roiCalcClosedTotal = closedJobs.reduce((total, expense) => total + expense.estimateTotal, 0);
				this.roiCalcClosedPercent = (this.roiExpensesTotal > 0) ? (this.roiCalcClosedTotal - this.roiExpensesTotal) / this.roiExpensesTotal : 0;
				break;

			case 'Invoices':
				this.roiCalcDescription = "Revenue";
				this.roiCalcOpenTotal = openJobs.reduce((total, expense) => total + expense.invoiceTotal, 0);
				this.roiCalcOpenPercent = (this.roiExpensesTotal > 0) ? (this.roiCalcOpenTotal - this.roiExpensesTotal) / this.roiExpensesTotal : 0;
				this.roiCalcClosedTotal = closedJobs.reduce((total, expense) => total + expense.invoiceTotal, 0);
				this.roiCalcClosedPercent = (this.roiExpensesTotal > 0) ? (this.roiCalcClosedTotal - this.roiExpensesTotal) / this.roiExpensesTotal : 0;
				break;

			case 'Payments Received':
				this.roiCalcDescription = "Revenue";
				this.roiCalcOpenTotal = openJobs.reduce((total, expense) => total + expense.paymentTotal, 0);
				this.roiCalcOpenPercent = (this.roiExpensesTotal > 0) ? (this.roiCalcOpenTotal - this.roiExpensesTotal) / this.roiExpensesTotal : 0;
				this.roiCalcClosedTotal = closedJobs.reduce((total, expense) => total + expense.paymentTotal, 0);
				this.roiCalcClosedPercent = (this.roiExpensesTotal > 0) ? (this.roiCalcClosedTotal - this.roiExpensesTotal)/ this.roiExpensesTotal : 0;
				break;

		}
	}

	protected getContactChecklists(businessDevelopmentContactId?: number): IChecklistGroupModel[] {
		if (!businessDevelopmentContactId)
			return [];

		const returnModel = this.businessDevelopmentContactsChecklistGroups().filter(x => x.businessDevelopmentContactId === businessDevelopmentContactId);
		return returnModel;
	}

	editContactChecklist(businessDevelopmentContactId: number) {
		this.spinnerStatus1 = "reset";
		this.contactChecklistGroups.set(this.getContactChecklists(businessDevelopmentContactId));

		this.contactChecklistDialogRef.showDialog();
	}

	async onSaveContactChecklist() {
		this.spinnerStatus1 = "spin";

		try {
			await this.checklistsService.updateChecklistItems(this.contactChecklistGroups());
			const allContactChecklistGroups = await this.checklistsService.getAllChecklistGroupsForBusinessDevelopmentContacts(this.businessDevelopmentAccount.businessDevelopmentAccountId);
			this.businessDevelopmentContactsChecklistGroups.set(allContactChecklistGroups);

			this.spinnerStatus1 = "ok";

			await SleepService.sleep(750);

			this.contactChecklistDialogRef.hideDialog();
		}
		catch {
			this.spinnerStatus1 = "error";
		}
	}

	onCancelContactChecklist() {
		this.contactChecklistDialogRef.hideDialog();
	}
}

